// parseFile.ts
import * as XLSX from 'xlsx';
import Papa from 'papaparse';
import mammoth from 'mammoth';
import type { ParsedFileResult } from './types';

const trim = (v: any) => (v === null || v === undefined ? '' : String(v).trim());

export async function parseFile(file: File): Promise<ParsedFileResult> {
  const name = file.name.toLowerCase();
  if (name.endsWith('.csv')) return parseCSV(file);
  if (name.endsWith('.xlsx') || name.endsWith('.xls')) return parseExcel(file);
  if (name.endsWith('.docx')) return parseWord(file);
  if (name.endsWith('.pdf')) return parsePDF(file);
  throw new Error(`Unsupported file format: ${file.name}`);
}

export async function parseCSV(file: File): Promise<ParsedFileResult> {
  const text = await file.text();
  
  return new Promise((resolve, reject) => {
    Papa.parse<string[]>(text, { 
      skipEmptyLines: true,
      complete: (results) => {
        try {
          const rows = results.data as string[][];
          const parsed = normalizeRows(rows);
          resolve(parsed);
        } catch (error) {
          reject(error);
        }
      },
      error: (error:any) => {
        reject(error);
      }
    });
  });
}

export async function parseExcel(file: File): Promise<ParsedFileResult> {
  const ab = await file.arrayBuffer();
  const workbook = XLSX.read(ab, { type: 'array' });
  const sheetNames = workbook.SheetNames;
  const sheets = sheetNames.map(name => {
    const ws = workbook.Sheets[name];
    const json = XLSX.utils.sheet_to_json<any[]>(ws, { header: 1, raw: false });
    return { name, rows: json as any[][] };
  });

  // default to first sheet
  return normalizeRows(sheets[0].rows, { sheets });
}

export async function parseWord(file: File): Promise<ParsedFileResult> {
  const arrayBuffer = await file.arrayBuffer();
  const result = await mammoth.extractRawText({ arrayBuffer });
  const lines = result.value.split(/\r?\n/).map(l => l.trim()).filter(Boolean);
  const rows = lines.map(line => {
    if (line.includes('\t')) return line.split('\t');
    if (line.includes('|')) return line.split('|');
    if (line.includes(',')) return line.split(',');
    return [line];
  });
  return normalizeRows(rows);
}

// Fixed PDF parsing using pdf-parse
export async function parsePDF(file: File): Promise<ParsedFileResult> {
  try {
    // Attempt to dynamically import pdf-parse and fall back to a runtime require if available
    let pdfParse: any;
    try {
      const pdfModule = await import('pdf-parse');
      pdfParse = pdfModule && (pdfModule.default || pdfModule);
    } catch (importError) {
      console.error('Failed to import pdf-parse via dynamic import:', importError);
      // Try runtime require if the environment provides it (avoid static require to satisfy TypeScript)
      const maybeRequire = (globalThis as any).require && typeof (globalThis as any).require === 'function'
        ? (globalThis as any).require
        : undefined;
      if (maybeRequire) {
        try {
          const pdfModule = maybeRequire('pdf-parse');
          pdfParse = pdfModule && (pdfModule.default || pdfModule);
        } catch (requireError) {
          console.error('Runtime require for pdf-parse failed:', requireError);
        }
      }
      if (!pdfParse) {
        throw new Error('PDF parsing library not available');
      }
    }

    if (typeof pdfParse !== 'function') {
      throw new Error('PDF parser is not a function');
    }

    const arrayBuffer = await file.arrayBuffer();
    const buffer = new Uint8Array(arrayBuffer);
    
    // Parse the PDF
    const data = await pdfParse(buffer);
    const text = data.text;
    
    if (!text || text.trim().length === 0) {
      throw new Error('No text content found in PDF');
    }
    
    // Enhanced text processing for tabular data
    const processedText = preprocessPDFText(text);
    const rows = extractRowsFromPDFText(processedText);
    
    return normalizeRows(rows);
  } catch (error) {
    console.error('PDF parsing failed:', error);
    throw new Error(`Unable to parse PDF file: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}

// Preprocess PDF text to improve table detection
function preprocessPDFText(text: string): string {
  return text
    .replace(/\r\n/g, '\n') // Normalize line endings
    .replace(/\n{3,}/g, '\n\n') // Limit consecutive newlines
    .replace(/[ \t]{2,}/g, ' ') // Replace multiple spaces/tabs with single space
    .trim();
}

// Extract rows from PDF text with better table detection
function extractRowsFromPDFText(text: string): string[][] {
  const lines = text.split('\n').map(line => line.trim()).filter(line => line.length > 0);
  
  if (lines.length === 0) {
    return [['No readable text found in PDF']];
  }

  // Try to detect if the PDF contains tabular data
  const likelyTabular = isLikelyTabularData(lines);
  
  if (likelyTabular) {
    return parseTabularPDF(lines);
  } else {
    // For non-tabular data, treat each line as a separate row with one column
    return lines.map(line => [line]);
  }
}

// Check if the PDF content looks like tabular data
function isLikelyTabularData(lines: string[]): boolean {
  if (lines.length < 3) return false;
  
  const sampleLines = lines.slice(0, Math.min(10, lines.length));
  
  // Count lines with multiple "words" separated by spaces
  const multiWordLines = sampleLines.filter(line => {
    const words = line.split(/\s+/).filter(word => word.length > 0);
    return words.length >= 2;
  });
  
  return multiWordLines.length >= sampleLines.length * 0.6;
}

// Parse PDF text that appears to be tabular data
function parseTabularPDF(lines: string[]): string[][] {
  const rows: string[][] = [];
  
  // Find the most common delimiter in the first few lines
  const sampleLines = lines.slice(0, Math.min(10, lines.length));
  const delimiter = detectDelimiter(sampleLines);
  
  // Parse all lines using the detected delimiter
  lines.forEach(line => {
    if (delimiter) {
      const cells = line.split(delimiter).map(cell => cell.trim());
      rows.push(cells);
    } else {
      // Fallback: split by multiple spaces
      const cells = line.split(/\s{2,}/).map(cell => cell.trim());
      rows.push(cells);
    }
  });
  
  return rows;
}

// Detect the most likely delimiter in tabular data
function detectDelimiter(lines: string[]): string | null {
  const delimiters = [',', '\t', '|', ';', '  ']; // Use string for double space
  let bestDelimiter: string | null = null;
  let maxScore = 0;
  
  delimiters.forEach(delimiter => {
    let score = 0;
    let consistent = true;
    let expectedColumns = -1;
    
    for (const line of lines) {
      const columns = line.split(delimiter).map(col => col.trim());
      
      // Filter out empty columns
      const nonEmptyColumns = columns.filter(col => col.length > 0);
      
      if (nonEmptyColumns.length < 2) {
        consistent = false;
        break;
      }
      
      if (expectedColumns === -1) {
        expectedColumns = nonEmptyColumns.length;
      } else if (nonEmptyColumns.length !== expectedColumns) {
        consistent = false;
        break;
      }
      
      score += nonEmptyColumns.length;
    }
    
    if (consistent && score > maxScore) {
      maxScore = score;
      bestDelimiter = delimiter;
    }
  });
  
  return bestDelimiter;
}

// parseFile.ts - normalizeRows function
function normalizeRows(rowsRaw: any[][], meta: any = {}): ParsedFileResult {
  const rows = rowsRaw || [];
  if (rows.length === 0) {
    return { vendorName: '', headers: [], rows: [], totalRows: 0, ...meta };
  }

  // Vendor name is A1 (row 0, col 0)
  const vendorName = trim(rows[0][0] ?? '');

  // Headers expected at row index 1 (second row)
  const rawHeaders = (rows[1] || []) as any[];
  
  // Generate Column_1, Column_2 etc. if headers are empty or invalid
  const headers = rawHeaders.map((h: any, idx: number) => {
    const trimmed = trim(h);
    return trimmed && trimmed !== '' ? trimmed : `Column_${idx + 1}`;
  });

  // Data rows start from row index 2; ensure each row has exactly headers.length columns
  const dataRows = rows.slice(2)
    .filter(row => row.some(cell => trim(cell) !== ''))
    .map(r => {
      const row = Array(headers.length).fill('');
      r.forEach((cell, index) => {
        if (index < headers.length) {
          row[index] = trim(cell);
        }
      });
      return row;
    });

  return {
    vendorName,
    headers,
    rows: dataRows,
    totalRows: dataRows.length,
    ...meta,
  };
}